
import Vue, { PropType } from 'vue';
import { get } from 'lodash';
import VideoPlayerCore from '@/core/VideoPlayerCore.vue';
import SideBar from '@/side-bar/SideBar.vue';
import VideoPlayerApi from '@/core/api';
import { Control } from '@/controls/control';
import { Widget } from '@/widgets/widget';
import predefinedControls from '@/controls/predefined-controls';
import predefinedWidgets from '@/widgets/predefined-widgets';
import EventBus, { Event, Unsubscriber } from '@/core/event-bus';
import { ResizeEmitter } from '@/core/resize-emitter';
import { PlayerConfigurationProp } from '@/config.interface';
import PlayerStateRepository, { PlayerState } from '@/core/player-state';
import { PortalTarget } from 'portal-vue';
import { v4 as uuid } from 'uuid';
import AnalyticsProvider from '@/core/analytics';
import { HlsConfig } from 'hls.js';
import { HiveConfig } from '@/core/hls/plugins/hive';
import { SideBarConfig } from '@/side-bar/side-bar-config.interface';

export default Vue.extend({
    name: 'VideoPlayer',

    components: {
        VideoPlayerCore,
        // TODO: The sidebar should be moved to the CorePlayer so this component is just
        // a thin layer to allow for seamless use of Vue and WC versions of the player
        SideBar,
        PortalTarget
    },

    props: {
        sourceUrl: {
            type: String as PropType<string>,
            required: true
        },

        useCredentials: {
            type: Boolean as PropType<boolean>,
            default: false
        },

        config: {
            type: Object as PropType<PlayerConfigurationProp>,
            default: null,
        },

        autoplay: {
            type: Boolean as PropType<boolean>,
            default: false,
        },

        preload: {
            type: Boolean as PropType<boolean>,
            default: false,
        },

        loopVideo: {
            type: Boolean as PropType<boolean>,
            default: false,
        },

        restrictMetadata: {
            type: Boolean as PropType<boolean>,
            default: false
        },

        metadataTypes: {
            type: Array as PropType<string[]>,
            default: () => ['spotme_metadata']
        },

        contentId: {
            type: String as PropType<string>,
            default: undefined
        },

        cmcdSessionId: {
            type: String as PropType<string>,
            default: undefined
        },

        hlsConfigOverwrites: {
            type: String as PropType<string>,
            default: undefined
        },

        eventBus: {
            type: Object as PropType<EventBus>,
            default: () => new EventBus()
        }
    },

    data() {
        const instanceId = uuid();
        const playerStateRepository = new PlayerStateRepository();
        const api = new VideoPlayerApi(this.eventBus, playerStateRepository, instanceId);
        const unsubscribers: Unsubscriber[] = [];
        const resizer = new ResizeEmitter(this.eventBus);
        const analyticsProvider = new AnalyticsProvider(
            this.eventBus,
            playerStateRepository
        );

        return {
            playerStateRepository,
            analyticsProvider,
            api,
            unsubscribers,
            resizer,
            verticalDisplay: false,

            widgets: [] as Widget[],
            controlsLeft: [] as Control[],
            controlsRight: [] as Control[],
            splitPaneHasContent: false
        };
    },

    computed: {
        configComponents() {
            let defaultComponents = ['invisible-overlay'];
            if (this.isIframeContent) {
                defaultComponents = [];
            }

            const components = [...defaultComponents, ...(this.config?.components ?? [])];
            return [...new Set(components)]; // removes duplicates
        },

        hasControlBar(): boolean {
            return !!(this.controlsLeft?.length ?? 0) || !!(this.controlsRight?.length ?? 0);
        },

        hideControlBar(): boolean {
            return !!this.config?.hideControlBar;
        },

        alwaysCollapseControlBarOnIdle(): boolean {
            return !!this.config?.alwaysCollapseControlBarOnIdle;
        },

        hideMouseOnIdle(): boolean {
            return !!this.config?.hideMouseOnIdle;
        },

        isIframeContent(): boolean {
            return !!this.config?.isIframeContent;
        },

        disableNativePip(): boolean {
            return this.config?.disableNativePip ?? true;
        },

        thumbnail(): string {
            return this.config?.thumbnail ?? '';
        },

        labels(): Record<string, Record<string, unknown>> {
            return this.config?.labels ?? {};
        },

        sideBarConfig(): SideBarConfig | undefined {
            return this.config?.sideBar;
        },

        isSideBarBelow(): boolean {
            return !!this.config?.isSideBarBelow;
        },

        isWebinar(): boolean {
            return !!this.config?.isWebinar;
        },

        darkMode(): Record<string, string | boolean> {
            return this.config?.darkMode ?? {};
        },

        noPlayer(): boolean {
            return !!this.config?.noPlayer;
        },

        sideBarNotificationPortalId(): string {
            return this.api.getPortalId('SIDEBAR_NOTIFICATION');
        },

        large(): boolean {
            return !!this.config?.large;
        },

        isVertical(): boolean {
            return this.large && this.verticalDisplay;
        },

        cssOverrides(): Record<string, unknown> {
            return {
                '--smvp-dark-mode-background-color': this.darkMode['background-color'],
                '--smvp-dark-mode-color': this.darkMode.color,
                '--smvp-dark-mode-border-color': this.darkMode['border-color'],
                '--smvp-tab-active-color': this.sideBarConfig?.theme?.tabHighlightColor,
                '--smvp-btn-color': this.sideBarConfig?.theme?.btnBackgroundColor || 'rgb(54, 135, 250)'
            };
        },
        widgetsInside(): Widget[] {
            return this.widgets.filter((widget) => widget.insideVideoElement?.({ isSideBarBelow: this.isSideBarBelow, hasSplitPanes: this.hasSplitPanes }) && !widget.config?.splitPane && !widget.config?.behindVideoElement);
        },
        widgetsOutside(): Widget[] {
            return this.widgets.filter((widget) => !widget.insideVideoElement?.({ isSideBarBelow: this.isSideBarBelow, hasSplitPanes: this.hasSplitPanes }) && !widget.config?.splitPane && !widget.config?.behindVideoElement);
        },
        widgetsSplitPane(): Widget[] {
            return this.widgets.filter((widget) => widget.config?.splitPane && !widget.config?.behindVideoElement);
        },
        widgetsBehind(): Widget[] {
            return this.widgets.filter((widget) => widget.config?.behindVideoElement);
        },

        hlsConfigOverwritesObject(): Partial<HlsConfig> | undefined {
            if (!this.hlsConfigOverwrites) {
                return undefined;
            }

            try {
                return JSON.parse(this.hlsConfigOverwrites);
            } catch (e) {
                console.error('Invalid hlsConfigOverwrites', e);
                return undefined;
            }
        },

        startPosition(): number {
            return this.hlsConfigOverwritesObject?.startPosition ?? 0;
        },

        hiveConfig(): HiveConfig {
            return this.config?.hiveConfig ?? {};
        },

        defaultSplitPaneSize(): number {
            return this.config?.defaultSplitPaneSize ?? 50;
        },

        hasSplitPanes(): boolean {
            return this.splitPaneHasContent && !!this.widgetsSplitPane.length;
        }
    },

    watch: {
        sourceUrl: {
            immediate: true,
            handler() {
                this.playerStateRepository.setState({
                    videoSource: this.sourceUrl
                });
            }
        },

        startPosition(newVal, oldVal) {
            if (newVal !== oldVal) {
                const currPosition = this.playerStateRepository?.state?.videoElapsedSecs ?? 0;
                const seekTo = Math.max(newVal ?? 0, currPosition);
                if (seekTo > currPosition) {
                    this.api.seek(seekTo);
                }
            }
        },
    },

    mounted() {
        this.buildControlsAndWidgets();

        const { unsubscriber: stateUnsubscriber } = this.playerStateRepository
            .onStateChange((state: Partial<PlayerState>) => {
                this.$emit('state-change', state);
            });

        this.unsubscribers.push(
            stateUnsubscriber,
            this.eventBus.on(Event.error, (error: unknown, context?: string) => {
                this.$emit('player-error', error, context);
            }),
            this.eventBus.on(Event.customAction, (payload: unknown, context?: string) => {
                this.$emit('custom-action', payload, context);
            }),
            this.eventBus.on(Event.metadata, (payload: unknown) => {
                this.$emit('metadata', payload);
            }),
            this.eventBus.on(Event.analytics, (payload: unknown, context?: string) => {
                this.$emit('player-analytics', payload, context);
            }),
            this.eventBus.on(Event.resize, (payload: unknown) => {
                const { width, height } = payload as { width: number, height: number };
                this.verticalDisplay = height > width;
            }),
        );

        (this.$refs?.videoPlayerCore as Vue)?.$el && this.resizer.init((this.$refs?.videoPlayerCore as Vue)?.$el);
    },

    beforeUpdate() {
        this.addGetApiToWCProps();

        this.buildControlsAndWidgets();
    },

    beforeDestroy() {
        for (const unsubscriber of this.unsubscribers) {
            if (typeof unsubscriber === 'function') {
                unsubscriber();
            }
        }

        this.analyticsProvider?.dispose();
    },

    methods: {
        buildControlsAndWidgets() {
            this.widgets = this.buildComponentArray(this.getWidget) as unknown as Widget[];

            this.controlsLeft = this.buildComponentArray(
                (id, config, hidden) => this.getControl(id, config, false, hidden)
            );

            this.controlsRight = this.buildComponentArray(
                (id, config, hidden) => this.getControl(id, config, true, hidden)
            );
        },

        buildComponentArray(
            getter: (
                id: string,
                config: Record<string, unknown>,
                hidden: boolean
            ) => Control | Widget | boolean
        ): Control[] | Widget[] {
            return this.configComponents
                .reduce(
                    (
                        final: Control[] | Widget[],
                        curr: string | { id: string }
                    ) => {
                        const id = typeof curr !== 'string'
                            ? get(curr, 'id', '')
                            : curr;
                        const config = get(curr, 'config', {});
                        const hidden = get(curr, 'hidden', false);
                        const c = getter(id, config, hidden);

                        return [
                            ...final,
                            ...(c ? [c] : [])
                        ];
                    }, []
                );
        },

        getControl(
            id: string,
            config: Record<string, unknown>,
            isAlignRight = false,
            hidden: boolean
        ): Control | boolean {
            const predefinedControl = get(predefinedControls, id);

            if (!predefinedControl) {
                return false;
            }

            const control = {
                ...this.withClientConfig(
                    predefinedControl,
                    config,
                ),
                labels: get(this.labels, id, {}),
                hidden
            } as Control;

            return !isAlignRight
                ? !get(predefinedControls, `${id}.rightAlign`) && control
                : get(predefinedControls, `${id}.rightAlign`) && control;
        },

        getWidget(
            id: string,
            config: Record<string, unknown>,
            hidden: boolean
        ): Widget {
            return {
                ...this.withClientConfig(
                    get(predefinedWidgets, id),
                    config,
                ),
                labels: get(this.labels, id, {}),
                hidden
            } as Widget;
        },

        withClientConfig(
            component: Control | Widget,
            config: Record<string, unknown>
        ): Control | Widget {
            return {
                ...component,
                config: {
                    startPosition: this.startPosition,
                    ...get(component, 'config', {}),
                    large: this.large,
                    ...config,
                }
            } as Control | Widget;
        },

        getApi(): VideoPlayerApi {
            return this.api;
        },

        // if this is a webcomponent, add the getApi method
        // to the WC props
        addGetApiToWCProps() {
            const root = this.$el.getRootNode() as unknown as {
                host: {
                    getApi: () => VideoPlayerApi
                }
            };

            if (
                root &&
                root instanceof ShadowRoot &&
                !get(root, 'host.getApi')
            ) {
                root.host.getApi = this.getApi;
            }
        },

        onSplitPaneHasContent(hasContent: boolean) {
            this.splitPaneHasContent = hasContent;
        }
    }
});
